/**
 * web/src/lib/nextStepsCopy.ts
 * The one-line "where is the Next Steps copy at" note on the Sponsor Approvals
 * next steps banner (2026-10-29). The team edits the Next Steps Doc often;
 * Pixel auto-syncs it ~10 min after the last edit, and drafts already built
 * keep their old copy until someone clicks Rebuild drafts. This turns the
 * stored template row + the waiting drafts into the sentence that says which
 * of those states we're in. Pure — the banner renders it.
 */
import { formatDistanceToNow } from 'date-fns';
import type { TemplateSourceView } from '../api/events';

export interface CopyNote {
  tone: 'info' | 'warn';
  text: string;
}

/** Server timestamps are naive UTC ISO strings; read them as UTC. */
function parseUtc(iso: string): Date {
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
}

/**
 * The note for the Next Steps banner.
 *
 * @param template the event's stored Next Steps template row (undefined = never synced)
 * @param draftBuiltAt when each waiting next steps draft was last built or rebuilt (ISO)
 * @returns the note, or null when there's nothing to say
 */
export function nextStepsCopyNote(
  template: TemplateSourceView | undefined,
  draftBuiltAt: string[],
): CopyNote | null {
  if (!template) {
    return { tone: 'warn', text: "The Next Steps Doc hasn't been synced yet. Click Sync copy." };
  }
  if (template.status === 'error') {
    return {
      tone: 'warn',
      text: `Pixel couldn't read the Next Steps Doc, so drafts use the previous copy. ${template.message ?? ''}`.trim(),
    };
  }
  if (template.message?.startsWith('Sync held')) {
    return { tone: 'warn', text: template.message };
  }
  if (template.status === 'stale') {
    return {
      tone: 'info',
      text: 'The Next Steps Doc was edited. Pixel syncs it about 10 minutes after the last edit, or click Sync copy.',
    };
  }
  if (!template.ingestedAt) return null;
  const synced = parseUtc(template.ingestedAt);
  const ago = formatDistanceToNow(synced, { addSuffix: true });
  const older = draftBuiltAt.filter((s) => parseUtc(s) < synced).length;
  if (older > 0) {
    return {
      tone: 'info',
      text: `Copy updated ${ago}. ${older} draft${older === 1 ? ' was' : 's were'} built before that, so click Rebuild drafts to update ${older === 1 ? 'it' : 'them'}.`,
    };
  }
  return { tone: 'info', text: `Copy synced from the Doc ${ago}.` };
}
